import { useOutsideClick } from "@/hooks/useOutsideClick";
import React, { useState, useRef, useEffect } from "react";
import { motion } from "motion/react";
import { toast } from "sonner";
import { getCookie } from "@/lib/cookies";
import { API_URL } from "@/env";
import { useWorkspaceStore } from "@/hooks/useWorkspace";
import CopyWhite from "@/assets/icons/CopyWhite.svg";
import { showCustomToast } from "../CustomToast";

const InviteUsersModal = ({
  setIsOpen,
}: //   onInviteSent,
{
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  //   onInviteSent?: () => void;
}) => {
  const token = getCookie("token");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef(null);

  const { currentWorkspace } = useWorkspaceStore();

  useOutsideClick(modalRef, () => {
    setIsOpen(false);
  });

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [setIsOpen]);

  const inviteLink = `https://app.usetool.bar/dashboard/${currentWorkspace?._id}`;

  const validateEmail = (email: string) => {
    if (!email.trim()) {
      setEmailError("");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email.trim())) {
      setEmailError("Invalid email address");
      return false;
    }

    setEmailError("");
    return true;
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    showCustomToast("Link copied to clipboard", "check");
  };

  const handleInvite = async () => {
    if (!validateEmail(email)) {
      return;
    }

    try {
      setIsLoading(true);

      const response = await fetch(
        `${API_URL}/workspaces/${currentWorkspace?._id}/invite`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            emails: [email.trim().toLowerCase()],
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to invite user");
      }

      const result = await response.json();

      // Show success/failure messages
      if (
        (result.invitationSent && result.invitationSent.length > 0) ||
        (result.signupLinkSent && result.signupLinkSent.length > 0)
      ) {
        showCustomToast("Invitation sent successfully", "check");
        setEmail("");
        setIsOpen(false);
        //    onInviteSent?.();
      }

      // if (result.notFound && result.notFound.length > 0) {
      //   toast.error(`User not found: ${result.notFound.join(", ")}`);
      // }

      if (result.alreadyMember && result.alreadyMember.length > 0) {
        showCustomToast(`User is already a member`, "exclamation");
      }

      if (result.alreadyInvited && result.alreadyInvited.length > 0) {
        showCustomToast(`User has already been invited`, "exclamation");
      }
    } catch (error) {
      console.error("Error inviting user:", error);
      toast.error("Failed to send invitation");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="onest-md bg-gray-50/[0.72] backdrop-blur-md z-[100000] fixed w-screen h-screen flex items-center justify-center"
    >
      <div
        className="p-6 flex flex-col rounded-[24px] w-[480px] bg-gray-200"
        ref={modalRef}
      >
        <div>
          <p className="text-[20px] leading-[28px] font-medium text-white">
            Add team members
          </p>
          <p className="mt-2 text-[14px] leading-[20px] text-white/[0.32] font-medium">
            Contribute to bug reporting together in the same workspace
          </p>

          <div className="mt-8">
            <p className="text-[14px] leading-[20px] text-white/[0.32] font-medium">
              Invite by email
            </p>
            <div className="flex gap-3 mt-2">
              <input
                type="email"
                autoFocus
                placeholder="mail@example.com"
                className={`rounded-[12px] h-[36px] flex-1 text-[14px] leading-[20px] px-4 outline-none ring-0 placeholder:text-white/[0.32] text-white bg-white/[0.04] border-[1px] ${
                  emailError ? "border-red-700" : "border-white/[0.08]"
                } py-[8px]`}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && email.length) {
                    handleInvite();
                  }
                }}
                disabled={isLoading}
              />
              <button
                onClick={handleInvite}
                disabled={!email.length || isLoading}
                className="h-[36px] px-4 disabled:text-white/[0.32] disabled:bg-white/[0.04] transition-all text-[14px] leading-[20px] font-medium bg-white hover:bg-white/[0.8] text-gray-50 rounded-[12px] flex items-center justify-center"
              >
                {isLoading ? "Inviting..." : "Invite"}
              </button>
            </div>
            {emailError && (
              <p className="text-red-700 text-[12px] leading-[16px] mt-2">
                {emailError}
              </p>
            )}
          </div>

          <div className="relative mt-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/[0.08]"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 bg-gray-200 text-[14px] leading-[20px] text-white/[0.32] font-medium">
                OR
              </span>
            </div>
          </div>

          <div className="mt-5">
            <p className="text-[14px] leading-[20px] text-white/[0.32] font-medium">
              Copy & share workspace link
            </p>
            <div
              className="mt-2 relative cursor-pointer"
              onClick={handleCopyLink}
            >
              <div className="rounded-[12px] h-[36px] w-full text-[14px] truncate leading-[20px] px-4 pr-12 outline-none ring-0 text-white bg-white/[0.04] border-[1px] border-white/[0.08] py-2">
                {inviteLink}
              </div>
              <button className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-[8px] transition-all">
                <img src={CopyWhite} className="w-5 h-5" alt="Copy" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default InviteUsersModal;
